import { CaretDownOutlined, CheckOutlined } from '@ant-design/icons';
import { Select, Checkbox, InputNumber, Tooltip } from 'antd';
import { useTranslation } from 'react-i18next';

import {
  SettingsOutlined,
  BrightnessOutlined,
  FreehandScalpelOutlined,
  RectScalpelOutlined,
  RemoveBedOutlined,
  Rotate3DOutlined,
  StraightenOutlined,
} from '@vindr/icons';

import Popover from './Popover';

const PROPERTY_TYPE = {
  QUALITY: 1,
  SHADE: 2,
  SAMPLE_DISTANCE: 3,
  RANGE: 4,
  AMBIENT: 5,
  DIFFUSE: 6,
  SPECULAR: 7,
  SPECULAR_POWER: 8,
  OPACITY_DISTANCE: 9,
};

const TOOLS_3D = {
  ROTATE: 1,
  ADJUST_WINDOW: 2,
  RESET: 3,
  CROSSHAIR: 4,
  DISTANCE: 5,
  RECT_SCALPEL: 6,
  FREEHAND_SCALPEL: 7,
};

const QUALITY = {
  NORMAL: 1,
  HIGH: 2,
};

const KEY = {
  DEL: 'Del',
  ENTER: 'Enter',
  ESC: 'Esc',
};

const toolButtonClass =
  'my-2 h-8 w-8 cursor-pointer flex justify-center items-center bg-secondary-dark rounded text-3xl hover:text-primary-light';

const VRToolsUI = ({
  activeTool,
  shade,
  ambient,
  diffuse,
  specularPower,
  specular,
  opacityDistance,
  quality,
  handleChangeProperty,
  handleActiveRotate3D,
  handleAdjustWindow,
  handleActiveDistance,
  handleRemoveBed,
  handleActiveRecTool,
  handleActiveFreehandTool,
}) => {
  const { t } = useTranslation('Vindoc');

  const advanceContent = (
    <div className="advance-content" onDoubleClick={(e) => e.stopPropagation()}>
      <div className="advance-list">
        <div className="adv-option mb-3 flex">
          <div
            style={{ minWidth: 96 }}
            className="property-name align-right mr-2"
          >
            Shade:
          </div>
          <div className="property-data">
            <Checkbox
              checked={shade}
              onChange={(e) =>
                handleChangeProperty(PROPERTY_TYPE.SHADE, e.target.checked)
              }
            />
          </div>
        </div>
        <div className="adv-option mb-3 flex">
          <div
            style={{ minWidth: 96 }}
            className="property-name align-right mr-2"
          >
            Ambient:
          </div>
          <div className="property-data">
            <InputNumber
              className="input-value"
              min={0}
              max={1}
              step={0.01}
              value={ambient}
              size="small"
              onChange={(value) =>
                handleChangeProperty(PROPERTY_TYPE.AMBIENT, value)
              }
            />
          </div>
        </div>
        <div className="adv-option mb-3 flex">
          <div
            style={{ minWidth: 96 }}
            className="property-name align-right mr-2"
          >
            Diffuse:
          </div>
          <div className="property-data">
            <InputNumber
              className="input-value"
              min={0}
              max={1}
              step={0.01}
              value={diffuse}
              size="small"
              onChange={(value) =>
                handleChangeProperty(PROPERTY_TYPE.DIFFUSE, value)
              }
            />
          </div>
        </div>
        <div className="adv-option mb-3 flex">
          <div
            style={{ minWidth: 96 }}
            className="property-name align-right mr-2"
          >
            Specular:
          </div>
          <div className="property-data">
            <InputNumber
              className="input-value"
              min={0}
              max={1}
              step={0.01}
              value={specular}
              size="small"
              onChange={(value) =>
                handleChangeProperty(PROPERTY_TYPE.SPECULAR, value)
              }
            />
          </div>
        </div>
        <div className="adv-option mb-3 flex">
          <div
            style={{ minWidth: 96 }}
            className="property-name align-right mr-2"
          >
            Specular power:
          </div>
          <div className="property-data">
            <InputNumber
              className="input-value"
              min={0}
              max={100}
              step={0.1}
              value={specularPower}
              size="small"
              onChange={(value) =>
                handleChangeProperty(PROPERTY_TYPE.SPECULAR_POWER, value)
              }
            />
          </div>
        </div>
        <div className="adv-option mb-3 flex">
          <div
            style={{ minWidth: 96 }}
            className="property-name align-right mr-2"
          >
            Opacity distance:
          </div>
          <div className="property-data">
            <InputNumber
              className="input-value"
              min={0}
              max={10}
              step={0.1}
              value={opacityDistance}
              size="small"
              onChange={(value) =>
                handleChangeProperty(PROPERTY_TYPE.OPACITY_DISTANCE, value)
              }
            />
          </div>
        </div>
        <div className="adv-option mb-3 flex">
          <div
            style={{ minWidth: 96 }}
            className="property-name align-right mr-2"
          >
            {t('Quality')}:
          </div>
          <div className="property-data">
            <Select
              size="small"
              popupClassName="dropdown-options-dark vindr-dropdown"
              menuItemSelectedIcon={<CheckOutlined />}
              suffixIcon={<CaretDownOutlined />}
              defaultValue={QUALITY.HIGH}
              value={quality}
              style={{ minWidth: 90 }}
              onChange={(value) =>
                handleChangeProperty(PROPERTY_TYPE.QUALITY, value)
              }
            >
              <Select.Option value={QUALITY.NORMAL}>{t('Normal')}</Select.Option>
              <Select.Option value={QUALITY.HIGH}>{t('High')}</Select.Option>
              {/* <Select.Option value="3">Highest</Select.Option> */}
            </Select>
          </div>
        </div>
      </div>
    </div>
  );

  return (
    <div
      className="toolbar-3d"
      style={{
        position: 'absolute',
        top: '50%',
        transform: 'translateY(-50%)',
        right: 5,
      }}
    >
      <div className="toolbar-3d-list">
        <Tooltip placement="left" title={t('3D Rotate')}>
          <div
            onClick={handleActiveRotate3D}
            className={`${toolButtonClass} ${
              activeTool === TOOLS_3D.ROTATE
                ? 'text-primary-light'
                : 'text-white'
            }`}
          >
            <Rotate3DOutlined />
          </div>
        </Tooltip>
        <Tooltip placement="left" title={t('Adjust Window')}>
          <div
            onClick={handleAdjustWindow}
            className={`${toolButtonClass} ${
              activeTool === TOOLS_3D.ADJUST_WINDOW
                ? 'text-primary-light'
                : 'text-white'
            }`}
          >
            <BrightnessOutlined />
          </div>
        </Tooltip>
        {/* <Tooltip title={t('Crosshairs')}>
            <div
              onClick={handleActiveCrosshairs}
              className={`${toolButtonClass} ${
                activeTool === TOOLS_3D.CROSSHAIR ? 'text-primary-light' : 'text-white'
              }`}
            >
              <CrossHairOutlined />
            </div>
          </Tooltip> */}
        <Tooltip placement="left" title={t('Length')}>
          <div
            onClick={handleActiveDistance}
            className={`${toolButtonClass} ${
              activeTool === TOOLS_3D.DISTANCE
                ? 'text-primary-light'
                : 'text-white'
            }`}
          >
            <StraightenOutlined />
          </div>
        </Tooltip>
        <Tooltip placement="left" title={t('Remove Bed')}>
          <div
            onClick={handleRemoveBed}
            className={toolButtonClass + ' text-white'}
          >
            <RemoveBedOutlined />
          </div>
        </Tooltip>
        <Tooltip placement="left" title={t('Rectangle Scalpel')}>
          <div
            onClick={handleActiveRecTool}
            className={`${toolButtonClass} ${
              activeTool === TOOLS_3D.RECT_SCALPEL
                ? 'text-primary-light'
                : 'text-white'
            }`}
          >
            <RectScalpelOutlined />
          </div>
        </Tooltip>
        <Tooltip placement="left" title={t('Freehand Scalpel')}>
          <div
            onClick={handleActiveFreehandTool}
            className={`${toolButtonClass} ${
              activeTool === TOOLS_3D.FREEHAND_SCALPEL
                ? 'text-primary-light'
                : 'text-white'
            }`}
          >
            <FreehandScalpelOutlined />
          </div>
        </Tooltip>
        <Popover
          placement="leftBottom"
          content={advanceContent}
          trigger="click"
          overlayClassName="popover-advance-option"
        >
          <div className={toolButtonClass + ' text-white'}>
            <SettingsOutlined />
          </div>
        </Popover>
        {/* <Tooltip title={t('Reset')}>
            <div onClick={handleReset3D} className={toolButtonClass}>
              <Icon name="refresh" />
            </div>
          </Tooltip> */}
      </div>
    </div>
  );
};

export default VRToolsUI;
